import { createSlice, createEntityAdapter, isAnyOf } from '@reduxjs/toolkit';
import {
  getContacts,
  getConversationById,
  getConversationMessages,
  getConversations,
  getMembersContacts,
  markConversationAsRead,
  syncMessages,
} from '../thunks/conversation';
import { CHAT_USER_STATUS } from 'src/common/Chat/constants/ChatUserStatus';
import { removeUser } from './user';
import { setCurrentWorkspace } from './currentWorkspace';

const contactsAdapter = createEntityAdapter();
const conversationsAdapter = createEntityAdapter();
const participantsAdapter = createEntityAdapter();

const initialState = {
  isLoading: true,
  error: null,
  contacts: contactsAdapter.getInitialState(),
  conversations: conversationsAdapter.getInitialState(),
  activeConversationId: null,
  participants: participantsAdapter.getInitialState(),
  recipients: [],
};

const slice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    stopLoading(state) {
      state.isLoading = false;
    },
    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // ON SEND MESSAGE
    addConversation(state, action) {
      const conversation = action.payload;
      const { conversationId, message, users, joined, ...conversationData } =
        conversation;

      const participants = users.map((user) => ({
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        profilePicture: user.profilePicture,
        email: user.email,
        lastActivity: user.lastUpdateConnect ?? null,
        status: user.isConnect
          ? CHAT_USER_STATUS.ONLINE
          : CHAT_USER_STATUS.OFFLINE,
      }));
      const participantsIds = participants.map((participant) => participant.id);

      const newMessage = {
        id: message.id,
        body: message.body,
        type: message.type,
        createdAt: message?.createdAt ?? new Date().toISOString(),
        attachments: [],
        senderId: message.senderId,
        metaData: message.metaData,
      };

      const newConversation = {
        id: conversationId,
        participants: participantsIds,
        type: participants.length > 2 ? 'GROUP' : 'ONE_TO_ONE',
        unread: Boolean(joined),
        messages: [newMessage],
        ...conversationData,
      };

      conversationsAdapter.addOne(state.conversations, newConversation);
      participantsAdapter.upsertMany(state.participants, participants);
    },

    addUsersToConversation(state, action) {
      const { conversationId, users, message } = action.payload;

      conversationsAdapter.updateOne(state.conversations, {
        id: conversationId,
        changes: {
          participants: state.conversations.entities[
            conversationId
          ].participants.concat(users.map((user) => user.id)),
          messages: state.conversations.entities[
            conversationId
          ].messages.concat({ ...message, attachments: [] }),
          type: 'GROUP',
        },
      });

      const newParticipants = users.map((user) => ({
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        profilePicture: user.profilePicture,
        email: user.email,
        lastActivity: user.lastUpdateConnect ?? null,
        status: user.isConnect
          ? CHAT_USER_STATUS.ONLINE
          : CHAT_USER_STATUS.OFFLINE,
      }));

      participantsAdapter.upsertMany(state.participants, newParticipants);
    },

    joinGroupConversation(state, action) {
      const { conversationId, users } = action.payload;

      const conversation = state.conversations.entities[conversationId];

      const newParticipants = users.map((user) => ({
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        profilePicture: user.profilePicture,
        email: user.email,
        lastActivity: user.lastUpdateConnect ?? null,
        status: user.isConnect
          ? CHAT_USER_STATUS.ONLINE
          : CHAT_USER_STATUS.OFFLINE,
      }));

      if (conversation) {
        conversationsAdapter.updateOne(state.conversations, {
          id: conversation.id,
          changes: {
            participants: users.map((user) => user.id),
            type: 'GROUP',
            unread: true,
          },
        });
      } else {
        conversationsAdapter.addOne(state.conversations, {
          id: conversationId,
          name: null,
          participants: users.map((user) => user.id),
          type: 'GROUP',
          messages: [],
          unread: true,
          joined: true,
        });
      }

      participantsAdapter.upsertMany(state.participants, newParticipants);
    },

    addMessage(state, action) {
      const { conversationId, message, currentUser } = action.payload;
      const conversation = state.conversations.entities[conversationId];

      if (!conversation) return;

      const newMessage = {
        id: message.id,
        body: message.body,
        type: message.type,
        createdAt: message?.createdAt ?? new Date().toISOString(),
        attachments: [],
        senderId: message.senderId,
        ...(message.temporary && { temporary: message.temporary }),
        metaData: message?.metaData ?? null,
      };

      const alreadyExists = conversation.messages.some(
        (m) => m.id === newMessage.id
      );
      if (!alreadyExists) {
        conversation.messages.push(newMessage);
      }

      if (newMessage.senderId !== currentUser) {
        conversation.unread = true;
      }
    },

    markMessageAsSent(state, action) {
      const { conversationId, message } = action.payload;

      const { temporaryId, ...newMessage } = message;

      const index = state.conversations.entities[
        conversationId
      ].messages.findIndex((message) => message.id === temporaryId);

      state.conversations.entities[conversationId].messages[index] = newMessage;
    },

    markMessageWithError(state, action) {
      const { conversationId, temporaryId } = action.payload;

      const index = state.conversations.entities[
        conversationId
      ].messages.findIndex((message) => message.id === temporaryId);

      state.conversations.entities[conversationId].messages[index] = {
        ...state.conversations.entities[conversationId].messages[index],
        error: true,
      };
    },

    finishConversation(state, action) {
      const { conversationId } = action.payload;

      conversationsAdapter.updateOne(state.conversations, {
        id: conversationId,
        changes: {
          finished: true,
        },
      });

      if (state.activeConversationId === conversationId) {
        state.activeConversationId = null;
      }
    },

    addRecipients(state, action) {
      const recipients = action.payload;
      state.recipients = recipients;
    },

    // RESET ACTIVE CONVERSATION
    resetActiveConversation(state) {
      state.activeConversationId = null;
    },

    setCurrentConversation(state, action) {
      state.activeConversationId = action.payload;
    },

    updateUserStatus(state, action) {
      const { userId, isConnect, lastUpdateConnect } = action.payload;
      participantsAdapter.updateOne(state.participants, {
        id: userId,
        changes: {
          status: isConnect
            ? CHAT_USER_STATUS.ONLINE
            : CHAT_USER_STATUS.OFFLINE,
          lastActivity: lastUpdateConnect,
        },
      });
    },

    ensureConversationUnread(state, action) {
      const { conversationId, situationId } = action.payload;
      const existing = state.conversations.entities[conversationId];
      if (existing) {
        existing.unread = true;
      } else {
        conversationsAdapter.upsertOne(state.conversations, {
          id: conversationId,
          unread: true,
          messages: [],
          participants: [],
          type: 'GROUP',
          ...(situationId && { situationId }),
        });
      }
    },

    resetChat(state) {
      state.isLoading = true;
      state.error = null;
      state.activeConversationId = null;
      state.recipients = [];
      conversationsAdapter.removeAll(state.conversations);
      contactsAdapter.removeAll(state.contacts);
      participantsAdapter.removeAll(state.participants);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getConversations.fulfilled, (state, action) => {
        const conversations = action.payload;

        const participants = conversations.reduce(
          (participants, conversation) => {
            const { users } = conversation;
            for (const user of users) {
              participants.set(user.id, {
                id: user.id,
                name: `${user.firstName} ${user.lastName}`,
                profilePicture: user.profilePicture,
                email: user.email,
                lastActivity: user.lastUpdateConnect,
                status: user.isConnect
                  ? CHAT_USER_STATUS.ONLINE
                  : CHAT_USER_STATUS.OFFLINE,
              });
            }
            return participants;
          },
          new Map()
        );

        const mapped = conversations.map(
          ({ users, isView, ...conversation }) => ({
            ...conversation,
            type: users.length > 2 ? 'GROUP' : 'ONE_TO_ONE',
            unread: !isView,
            participants: users.map((user) => user.id),
          })
        );

        conversationsAdapter.upsertMany(state.conversations, mapped);
        participantsAdapter.upsertMany(
          state.participants,
          Array.from(participants.values())
        );
      })
      .addCase(getConversationMessages.fulfilled, (state, action) => {
        const { messages } = action.payload;
        const { conversationId } = action.meta.arg;

        const conversationMessages =
          state.conversations.entities[conversationId].messages;

        conversationsAdapter.updateOne(state.conversations, {
          id: conversationId,
          changes: {
            messages: messages.concat(conversationMessages),
          },
        });
      })
      .addCase(markConversationAsRead.fulfilled, (state, action) => {
        const { conversationId } = action.meta.arg;
        const conversation = state.conversations.entities[conversationId];
        if (conversation) {
          conversation.unread = false;
        }
      })
      .addCase(syncMessages.fulfilled, (state, action) => {
        const { conversationId, messages } = action.payload;
        conversationsAdapter.updateOne(state.conversations, {
          id: conversationId,
          changes: {
            messages: [
              ...state.conversations.entities[conversationId].messages,
              ...messages,
            ],
          },
        });
      })
      .addCase(getConversationById.fulfilled, (state, action) => {
        const conversation = action.payload;
        const temporaryConversation =
          state.conversations.entities[conversation.id];

        if (temporaryConversation?.joined) {
          const newConversation = {
            ...state.conversations.entities[conversation.id],
            unread: !conversation.isView,
            participants: conversation.users.map((user) => user.id),
            messages: conversation.messages.map((message) => ({
              id: message.id,
              body: message.body,
              type: message.type,
              createdAt: message?.createdAt ?? new Date().toISOString(),
              attachments: [],
              senderId: message.senderId,
              metaData: message.metaData,
            })),
            situationId: conversation.situationId,
          };

          delete newConversation.joined;

          conversationsAdapter.setOne(state.conversations, newConversation);
        } else {
          const participants = conversation.users.map((user) => ({
            id: user.id,
            name: `${user.firstName} ${user.lastName}`,
            profilePicture: user.profilePicture,
            email: user.email,
            lastActivity: user.lastUpdateConnect ?? null,
            status: user.isConnect
              ? CHAT_USER_STATUS.ONLINE
              : CHAT_USER_STATUS.OFFLINE,
          }));

          const participantsIds = participants.map(
            (participant) => participant.id
          );

          const newConversation = {
            ...conversation,
            id: conversation.id,
            participants: participantsIds,
            type: participants.length > 2 ? 'GROUP' : 'ONE_TO_ONE',
            unread: false,
            messages: conversation.messages.map((message) => ({
              id: message.id,
              body: message.body,
              type: message.type,
              createdAt: message?.createdAt ?? new Date().toISOString(),
              attachments: [],
              senderId: message.senderId,
              metaData: message.metaData,
            })),
          };

          conversationsAdapter.addOne(state.conversations, newConversation);
          participantsAdapter.upsertMany(state.participants, participants);
        }
      })
      .addCase(getConversationById.rejected, (state, action) => {
        if (state.conversations.entities[action.meta.arg.conversationId]) {
          state.conversations.entities[action.meta.arg.conversationId].error =
            true;
        }
      })
      .addMatcher(
        isAnyOf(getContacts.fulfilled, getMembersContacts.fulfilled),
        (state, action) => {
          const contacts = action.payload;

          const mapped = contacts.map(
            ({ id, email, profilePicture, ...contact }) => ({
              id,
              name: `${contact.firstName} ${contact.lastName}`,
              profilePicture,
              email,
              lastActivity: contact.updatedAt,
              status: contact.isConnect
                ? CHAT_USER_STATUS.ONLINE
                : CHAT_USER_STATUS.OFFLINE,
              ...(action.type === 'chat/getMembersContacts/fulfilled' && {
                isMember: true,
              }),
            })
          );

          contactsAdapter.upsertMany(state.contacts, mapped);
        }
      )
      .addMatcher(isAnyOf(removeUser, setCurrentWorkspace), (state) => {
        state.isLoading = true;
        state.error = null;
        state.activeConversationId = null;
        state.recipients = [];
        conversationsAdapter.removeAll(state.conversations);
        contactsAdapter.removeAll(state.contacts);
        participantsAdapter.removeAll(state.participants);
      });
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  startLoading,
  stopLoading,
  hasError,
  addRecipients,
  addConversation,
  addUsersToConversation,
  joinGroupConversation,
  finishConversation,
  addMessage,
  ensureConversationUnread,
  markMessageAsSent,
  markMessageWithError,
  resetActiveConversation,
  setCurrentConversation,
  updateUserStatus,
  resetChat,
} = slice.actions;

// Selectors
export const contactsAdapterSelectors = contactsAdapter.getSelectors();
export const participantsAdapterSelectors = participantsAdapter.getSelectors();
export const conversationsAdapterSelectors =
  conversationsAdapter.getSelectors();
